import React, { useState } from 'react';
import { Settings, Crown, Moon, Sun, ExternalLink, Mail, MessageSquare, BookOpen, Sparkles, ChevronDown, ChevronRight, Share2 } from 'lucide-react';
import { Modal, ModalContent, ModalHeader } from '../shared/Modal';
import { DeleteAccountModal } from './DeleteAccountModal';
import { FeedbackModal } from './FeedbackModal';
import { WhatsNewModal } from './WhatsNewModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { DASHBOARD_VIEW_OPTIONS } from '../../config/constants';
import { DashboardView, WidgetKey } from '../../types';
import { hasUnseenUpdates, LATEST_VERSION } from '../../config/changelog';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReplayTutorial?: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, onReplayTutorial }) => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { user, paymentTier, sheetId } = useAuth();
  const { userSettings, updateDashboardView, toggleWidgetVisibility } = useFinancialData();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDangerZone, setShowDangerZone] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showWhatsNewModal, setShowWhatsNewModal] = useState(false);

  const isPro = paymentTier === 'pro';
  const handleShare = async () => {
    const shareData = {
      title: 'TallyUp Finance',
      text: 'Track your net worth, investments, and budget all in one place.',
      url: 'https://www.tallyup.finance',
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
      }
    } catch {
      // User cancelled or share failed silently
    }
  };

  const cardClasses = `rounded-xl p-4 ${
    darkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'
  }`;

  const linkClasses = `flex justify-between items-center px-4 py-2.5 w-full transition-colors ${
    darkMode ? 'hover:bg-white/5' : 'hover:bg-slate-100'
  }`;

  const linkTextClasses = `text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`;
  const linkIconClasses = darkMode ? 'text-slate-500' : 'text-slate-400';
  const dividerClasses = darkMode ? 'border-t border-white/10' : 'border-t border-slate-200';

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} maxWidth="2xl">
        <ModalContent>
          <ModalHeader
            title="Settings"
            icon={
              <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center ${
                darkMode ? 'bg-violet-500/20' : 'bg-violet-100'
              }`}>
                <Settings size={24} className={darkMode ? 'text-violet-400' : 'text-violet-600'} />
              </div>
            }
          />

          {/* Two-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left column */}
            <div className="space-y-4">
              {/* Account Info */}
              <div>
                <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Account</h4>
                <div className={cardClasses}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Email</span>
                    <span className={`text-sm font-medium truncate ml-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {user?.email || 'Unknown'}
                    </span>
                  </div>
                  <div className={`my-2.5 ${dividerClasses}`} />
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Plan</span>
                    <div className="flex items-center gap-1.5">
                      <Crown size={14} className={isPro ? 'text-yellow-500' : 'text-slate-400'} />
                      <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {isPro ? 'Pro' : 'Free'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Your Data */}
              {sheetId && (
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Your Data</h4>
                  <div className={cardClasses}>
                    <a
                      href={`https://docs.google.com/spreadsheets/d/${sheetId}/edit`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between gap-3"
                    >
                      <div className="flex items-start gap-3">
                        <svg width={28} height={28} viewBox="0 0 87.3 78" className="flex-shrink-0">
                          <path d="M6.6 66.85L14.1 78H73.2L80.7 66.85z" fill="#0066DA" />
                          <path d="M43.65 25L29.2 0H0L14.4 25z" fill="#00AC47" />
                          <path d="M73.2 78L87.3 53H58.1L43.65 78z" fill="#EA4335" />
                          <path d="M29.2 0L14.4 25L43.65 78L58.1 53z" fill="#00832D" />
                          <path d="M43.65 25L58.1 53H87.3L73.2 25z" fill="#2684FC" />
                          <path d="M6.6 66.85L43.65 25L14.4 25z" fill="#FFBA00" />
                        </svg>
                        <div>
                          <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                            Open in Google Sheets
                          </p>
                          <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Your data lives in your Google Drive
                          </p>
                        </div>
                      </div>
                      <ExternalLink size={14} className={darkMode ? 'text-slate-400 flex-shrink-0' : 'text-slate-500 flex-shrink-0'} />
                    </a>
                  </div>
                </div>
              )}

              {/* Appearance */}
              <div>
                <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Appearance</h4>
                <div className={cardClasses}>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      {darkMode ? <Moon size={16} className="text-violet-400" /> : <Sun size={16} className="text-yellow-500" />}
                      <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>Dark Mode</span>
                    </div>
                    <button
                      role="switch"
                      aria-checked={darkMode}
                      onClick={toggleDarkMode}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        darkMode ? 'bg-violet-500' : 'bg-slate-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                        darkMode ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Dashboard View */}
              <div>
                <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Dashboard View</h4>
                <div className={cardClasses}>
                  <div className="space-y-2">
                    {DASHBOARD_VIEW_OPTIONS.map((option) => {
                      const isActive = userSettings.dashboardView === option.value;
                      return (
                        <button
                          key={option.value}
                          onClick={() => sheetId && updateDashboardView(option.value as DashboardView, sheetId)}
                          className={`w-full text-left px-3 py-2.5 rounded-lg transition-all ${
                            isActive
                              ? darkMode
                                ? 'bg-violet-500/20 border border-violet-500/40'
                                : 'bg-violet-100 border border-violet-300'
                              : darkMode
                                ? 'hover:bg-white/5 border border-transparent'
                                : 'hover:bg-slate-100 border border-transparent'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className={`text-sm font-semibold ${
                              isActive
                                ? darkMode ? 'text-violet-300' : 'text-violet-700'
                                : darkMode ? 'text-white' : 'text-slate-900'
                            }`}>
                              {option.label}
                            </span>
                            {isActive && (
                              <span className={`text-xs font-medium ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>Active</span>
                            )}
                          </div>
                          <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            {option.description}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-4">
              {/* What's New */}
              <div>
                <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Updates</h4>
                <div className={`rounded-xl overflow-hidden ${
                  darkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'
                }`}>
                  <button onClick={() => setShowWhatsNewModal(true)} className={linkClasses}>
                    <span className="flex items-center gap-2">
                      <span className={linkTextClasses}>What's New</span>
                      <span className={`text-[11px] font-semibold tabular-nums ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        v{LATEST_VERSION}
                      </span>
                      {hasUnseenUpdates() && (
                        <span className="h-2 w-2 rounded-full bg-red-500 flex-shrink-0" />
                      )}
                    </span>
                    <Sparkles size={14} className={linkIconClasses} />
                  </button>
                </div>
              </div>

              {/* Support */}
              <div>
                <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Support</h4>
                <div className={`rounded-xl overflow-hidden ${
                  darkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'
                }`}>
                  <button onClick={() => setShowFeedbackModal(true)} className={linkClasses}>
                    <span className={linkTextClasses}>Submit Feedback</span>
                    <MessageSquare size={14} className={linkIconClasses} />
                  </button>
                  <div className={dividerClasses} />
                  <a href="mailto:support@tallyup.finance" className={linkClasses}>
                    <span className={linkTextClasses}>Contact Support</span>
                    <Mail size={14} className={linkIconClasses} />
                  </a>
                  {onReplayTutorial && (
                    <>
                      <div className={dividerClasses} />
                      <button onClick={() => { onReplayTutorial(); onClose(); }} className={linkClasses}>
                        <span className={linkTextClasses}>Replay Tutorial</span>
                        <BookOpen size={14} className={linkIconClasses} />
                      </button>
                    </>
                  )}
                  <div className={dividerClasses} />
                  <button onClick={handleShare} className={linkClasses}>
                    <span className={linkTextClasses}>Share TallyUp</span>
                    <Share2 size={14} className={linkIconClasses} />
                  </button>
                </div>
              </div>

              {/* Legal */}
              <div>
                <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Legal</h4>
                <div className={`rounded-xl overflow-hidden ${
                  darkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'
                }`}>
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className={linkClasses}>
                    <span className={linkTextClasses}>Privacy Policy</span>
                    <ExternalLink size={14} className={linkIconClasses} />
                  </a>
                  <div className={dividerClasses} />
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className={linkClasses}>
                    <span className={linkTextClasses}>Terms of Service</span>
                    <ExternalLink size={14} className={linkIconClasses} />
                  </a>
                </div>
              </div>

              {/* Danger Zone */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-red-500 mb-2">Danger Zone</h4>
                <div className={`rounded-xl overflow-hidden border ${
                  darkMode ? 'border-red-500/30 bg-red-500/5' : 'border-red-200 bg-red-50'
                }`}>
                  <button
                    onClick={() => setShowDangerZone(!showDangerZone)}
                    className={`flex justify-between items-center w-full px-4 py-2.5 transition-colors ${
                      darkMode ? 'hover:bg-red-500/10' : 'hover:bg-red-100/50'
                    }`}
                  >
                    <span className={`text-sm font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>Delete Account</span>
                    {showDangerZone
                      ? <ChevronDown size={14} className="text-red-500" />
                      : <ChevronRight size={14} className="text-red-500" />
                    }
                  </button>
                  {showDangerZone && (
                    <div className={`px-4 pb-3 ${darkMode ? 'border-t border-red-500/20' : 'border-t border-red-200'}`}>
                      <p className={`text-xs my-2.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Permanently delete your account and profile.
                      </p>
                      <button
                        onClick={() => setShowDeleteModal(true)}
                        className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors"
                      >
                        Delete Account
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Widget Visibility — full width */}
          <div className="mt-4">
            <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Visible Widgets</h4>
            <div className={cardClasses}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {([
                  { key: 'retirement401k' as WidgetKey, label: '401(k)' },
                  { key: 'assets' as WidgetKey, label: 'Assets' },
                  { key: 'banking' as WidgetKey, label: 'Banking' },
                  { key: 'budget' as WidgetKey, label: 'Budget' },
                  { key: 'creditCards' as WidgetKey, label: 'Credit Cards' },
                  { key: 'creditScores' as WidgetKey, label: 'Credit Scores' },
                  { key: 'crypto' as WidgetKey, label: 'Crypto' },
                  { key: 'goals' as WidgetKey, label: 'Goals' },
                  { key: 'hsa' as WidgetKey, label: 'HSA' },
                  { key: 'investments' as WidgetKey, label: 'Investments' },
                  { key: 'liabilities' as WidgetKey, label: 'Liabilities' },
                  { key: 'privateEquity' as WidgetKey, label: 'Private Equity' },
                ]).map(({ key, label }) => {
                  const isVisible = !(userSettings.hiddenWidgets || []).includes(key);
                  return (
                    <button
                      key={key}
                      onClick={() => sheetId && toggleWidgetVisibility(key, sheetId)}
                      className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        isVisible
                          ? darkMode
                            ? 'bg-white/10 text-white'
                            : 'bg-slate-100 text-slate-900'
                          : darkMode
                            ? 'bg-white/5 text-slate-600 line-through'
                            : 'bg-slate-50 text-slate-400 line-through'
                      }`}
                    >
                      <span className="truncate">{label}</span>
                      <div className={`relative inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors ${
                        isVisible ? 'bg-violet-500' : darkMode ? 'bg-slate-700' : 'bg-slate-300'
                      }`}>
                        <span className={`inline-block h-3 w-3 rounded-full bg-white transition-transform ${
                          isVisible ? 'translate-x-3.5' : 'translate-x-0.5'
                        }`} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Close button */}
          <div className="mt-5">
            <button
              onClick={onClose}
              className={`w-full px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors ${
                darkMode
                  ? 'bg-white/10 hover:bg-white/20 text-white'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-800'
              }`}
            >
              Close
            </button>
            <p className={`mt-3 text-center text-[11px] tabular-nums ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
              TallyUp v{LATEST_VERSION}
            </p>
          </div>
        </ModalContent>
      </Modal>

      {showDeleteModal && (
        <DeleteAccountModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
        />
      )}

      {showFeedbackModal && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
        />
      )}

      {showWhatsNewModal && (
        <WhatsNewModal
          isOpen={showWhatsNewModal}
          onClose={() => setShowWhatsNewModal(false)}
        />
      )}
    </>
  );
};
